/**
 * Analytics Intelligence Service Client
 *
 * Connects to the Python FastAPI analytics service (port 5001)
 * for ML-powered anomaly detection, risk scoring, spatial heatmaps,
 * and trend analysis.
 */
import axios from 'axios';

const ANALYTICS_URL = import.meta.env.VITE_ANALYTICS_URL || 'http://localhost:5001';

const analyticsClient = axios.create({
  baseURL: ANALYTICS_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

// ============================================================================
// TYPES (matching Python Pydantic models in apps/analytics/src/main.py)
// ============================================================================

export interface TrackStatistics {
  tenant_id: string;
  period: string;
  total_tracks: number;
  unique_tracks: number;
  by_classification: Record<string, number>;
  by_sensor: Record<string, number>;
  avg_duration_seconds: number | null;
  avg_altitude: number | null;
  avg_speed: number | null;
  geofence_breaches: number;
}

export interface HeatmapCell {
  lat: number;
  lng: number;
  count: number;
  intensity: number; // 0-1 normalized
}

export interface HeatmapResponse {
  cells: HeatmapCell[];
  min_lat: number;
  max_lat: number;
  min_lng: number;
  max_lng: number;
  resolution: number;
}

export interface RiskFactor {
  factor: string;
  score: number;
  description: string;
}

export interface RiskScore {
  track_id: string;
  score: number; // 0-100
  factors: RiskFactor[];
  timestamp: string;
}

export interface AnomalyDetection {
  track_id: string;
  anomaly_score: number;
  is_anomaly: boolean;
  reasons: string[];
}

export interface TrendDataPoint {
  period: string;
  unique_tracks: number;
  total_points: number;
  avg_altitude: number | null;
  avg_speed: number | null;
}

export interface TrendSummary {
  total_periods: number;
  avg_tracks_per_period: number;
  max_tracks: number;
  min_tracks: number;
  peak_period: string;
  trend: 'increasing' | 'decreasing';
}

export interface TrendAnalysis {
  period: string;
  data_points: TrendDataPoint[];
  summary: TrendSummary;
}

export interface HeatmapOptions {
  resolution?: number;
  minLat?: number;
  maxLat?: number;
  minLng?: number;
  maxLng?: number;
}

// ============================================================================
// API CLIENT
// ============================================================================

export const intelligenceApi = {
  /**
   * Health check for the analytics service
   */
  getHealth: async (): Promise<{ status: string; timestamp: string }> => {
    const response = await analyticsClient.get('/health');
    return response.data;
  },

  /**
   * Aggregate track statistics for a time range
   * GET /stats/{tenant_id}?start_time=...&end_time=...
   */
  getStats: async (
    tenantId: string,
    startTime: string,
    endTime: string
  ): Promise<TrackStatistics> => {
    const response = await analyticsClient.get(`/stats/${tenantId}`, {
      params: { start_time: startTime, end_time: endTime },
    });
    return response.data;
  },

  /**
   * Spatial activity heatmap (PostGIS grid)
   * GET /heatmap/{tenant_id}?start_time=...&end_time=...&resolution=...
   */
  getHeatmap: async (
    tenantId: string,
    startTime: string,
    endTime: string,
    options?: HeatmapOptions
  ): Promise<HeatmapResponse> => {
    const response = await analyticsClient.get(`/heatmap/${tenantId}`, {
      params: {
        start_time: startTime,
        end_time: endTime,
        resolution: options?.resolution ?? 0.01,
        min_lat: options?.minLat,
        max_lat: options?.maxLat,
        min_lng: options?.minLng,
        max_lng: options?.maxLng,
      },
    });
    return response.data;
  },

  /**
   * Multi-factor risk score for a specific track
   * GET /risk/{tenant_id}/{track_id}
   */
  getRiskScore: async (
    tenantId: string,
    trackId: string
  ): Promise<RiskScore> => {
    const response = await analyticsClient.get(
      `/risk/${tenantId}/${trackId}`
    );
    return response.data;
  },

  /**
   * ML anomaly detection (Isolation Forest)
   * GET /anomalies/{tenant_id}?start_time=...&end_time=...
   */
  getAnomalies: async (
    tenantId: string,
    startTime: string,
    endTime: string
  ): Promise<AnomalyDetection[]> => {
    const response = await analyticsClient.get(`/anomalies/${tenantId}`, {
      params: { start_time: startTime, end_time: endTime },
    });
    return response.data;
  },

  /**
   * Time-series trend analysis
   * GET /trends/{tenant_id}?start_time=...&end_time=...&granularity=...
   */
  getTrends: async (
    tenantId: string,
    startTime: string,
    endTime: string,
    granularity: 'hour' | 'day' | 'week' = 'day'
  ): Promise<TrendAnalysis> => {
    const response = await analyticsClient.get(`/trends/${tenantId}`, {
      params: { start_time: startTime, end_time: endTime, granularity },
    });
    return response.data;
  },
};

// ============================================================================
// UTILITIES
// ============================================================================

export type TimeRange = '24h' | '7d' | '30d' | '90d';

/**
 * Calculate the ISO start/end timestamps for a given time range
 */
export function getTimeWindow(range: TimeRange): {
  startTime: string;
  endTime: string;
} {
  const now = new Date();
  const start = new Date(now);
  switch (range) {
    case '24h':
      start.setHours(now.getHours() - 24);
      break;
    case '7d':
      start.setDate(now.getDate() - 7);
      break;
    case '30d':
      start.setDate(now.getDate() - 30);
      break;
    case '90d':
      start.setDate(now.getDate() - 90);
      break;
  }
  return { startTime: start.toISOString(), endTime: now.toISOString() };
}

/**
 * Get the previous period of the same duration (for trend comparison)
 */
export function getPreviousTimeWindow(range: TimeRange): {
  startTime: string;
  endTime: string;
} {
  const current = getTimeWindow(range);
  const currentStart = new Date(current.startTime);
  const currentEnd = new Date(current.endTime);
  const durationMs = currentEnd.getTime() - currentStart.getTime();
  const prevEnd = new Date(currentStart);
  const prevStart = new Date(prevEnd.getTime() - durationMs);
  return { startTime: prevStart.toISOString(), endTime: prevEnd.toISOString() };
}

/**
 * Map a time range to the appropriate trend granularity
 */
export function getGranularity(
  range: TimeRange
): 'hour' | 'day' | 'week' {
  switch (range) {
    case '24h':
      return 'hour';
    case '7d':
      return 'day';
    case '30d':
      return 'day';
    case '90d':
      return 'week';
  }
}

/**
 * Check if the analytics service is available
 */
export async function checkAnalyticsHealth(): Promise<boolean> {
  try {
    await intelligenceApi.getHealth();
    return true;
  } catch {
    return false;
  }
}
